/**
 * What the pipeline has been doing for the last two months, and what the pattern means.
 *
 * ⚠️ THE READING IS THE POINT, not the sparklines. Five lines going up and down is a chart nobody
 * acts on; `trendShape` says which of three things is happening — the top of the funnel shrank,
 * candidates are not getting through a stage, or the slowdown is on its way and nothing late has
 * moved yet. That last one is the one worth catching, because everything still looks fine.
 *
 * ⚠️ No conversion rates, deliberately — see `utils/pipelineTrends.ts`. A ratio over one fortnight
 * divides two different cohorts and is least trustworthy exactly when volume is moving.
 */
import { AlertCircle, ArrowDownRight, ArrowRight, ArrowUpRight, Minus } from 'lucide-react';
import type { Direction, Movement, TrendShape } from '../../../utils/pipelineTrends';
import { Section } from './trackingUi';

const ICON: Record<Direction, typeof ArrowUpRight> = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: ArrowRight,
  unknown: Minus,
};

/*
 * ⚠️ Up is not green and down is not red. More applications is good, more time-to-offer is not,
 * and this tab already spends green and amber on delivery against plan — a second scale meaning
 * something else is two colour systems on one page. Direction is carried by the arrow.
 */
const TONE: Record<Direction, string> = {
  up: 'text-gray-900',
  down: 'text-amber-700',
  flat: 'text-gray-500',
  unknown: 'text-gray-400',
};

export function TrendsSection({
  moves,
  shape,
  loading,
  error,
}: {
  moves: Movement[];
  shape: TrendShape;
  /** ⚠️ Required, not optional with a default — see the note below. */
  loading: boolean;
  error: string | null;
}) {
  /*
   * ⚠️ STILL READING IS NOT COULD NOT BE READ (CodeRabbit, PR #637). The hook has no data in
   * either state, so a section branching on `moves.length` alone announced a failed read for the
   * first second of every page load. `loading` is a required prop rather than one with a default:
   * a default of `false` is exactly the bug, and a caller that forgets it would reintroduce it
   * silently.
   */
  if (loading) {
    return (
      <Section title="What the pipeline has been doing">
        <p className="mt-2 text-sm text-gray-500">Reading the weekly figures…</p>
      </Section>
    );
  }

  if (error || !moves.length) {
    return (
      <Section title="What the pipeline has been doing">
      <div className="mt-2 flex items-start gap-2 text-sm text-gray-600">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <span>
          The weekly pipeline figures could not be read{error ? `: ${error}` : ''}. Everything else
          on this tab is unaffected — it comes from the Hiring Database, not the warehouse.
        </span>
      </div>
      </Section>
    );
  }

  return (
    <Section
      title="What the pipeline has been doing"
      aside="Eight weeks · last 2 against the 2 before"
    >
    <div className="mt-3 space-y-3">
      <div
        data-testid="trend-shape"
        className="rounded-xl border border-gray-200 bg-white px-5 py-4"
      >
        <h3 className="text-sm font-semibold text-gray-900">{shape.headline}</h3>
        <p className="mt-1 max-w-[80ch] text-sm leading-6 text-gray-600">{shape.detail}</p>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {moves.map((m) => {
          const Icon = ICON[m.direction];
          return (
            <div
              key={m.key}
              data-testid={`trend-${m.key}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-100 px-5 py-2.5 last:border-b-0"
            >
              <span className="w-40 shrink-0 text-sm text-gray-900">{m.label}</span>
              <Sparkline series={m.series} />
              <span className="ml-auto flex items-baseline gap-2 text-sm tabular-nums">
                <span className="text-gray-500">
                  {m.recent} <span className="text-xs">last 2 wks</span>
                </span>
                <span className={`flex items-center gap-1 font-medium ${TONE[m.direction]}`}>
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {m.changePct == null
                    ? 'too few'
                    : `${m.changePct > 0 ? '+' : ''}${m.changePct}%`}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      <p className="text-xs leading-relaxed text-gray-500">
        Each figure is the last <b>two weeks</b> against the two before, in rolling seven-day
        periods rather than calendar weeks — a calendar week in progress is a part-week, and a
        dashboard built on those shows everything falling for six days out of seven. Counts cover
        every Lever posting, open or closed: narrowing a <i>series</i> to today&apos;s open roles
        would rewrite its own history each time a role closed.
      </p>
    </div>
    </Section>
  );
}

/** Eight weekly points, oldest first. Inline SVG — see `PlanWaterfall` for why no chart library. */
function Sparkline({ series }: { series: number[] }) {
  if (series.length < 2) return <span className="flex-1" />;

  const W = 150;
  const H = 24;
  // Floored at 1 so a flat zero series draws along the bottom instead of dividing by nothing.
  const max = Math.max(...series, 1);
  const x = (i: number) => (i * W) / (series.length - 1);
  const y = (v: number) => H - 2 - (v / max) * (H - 4);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-6 w-[150px] shrink-0"
      role="img"
      aria-label={`Last ${series.length} weeks, oldest first: ${series.join(', ')}`}
    >
      <polyline
        points={series.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
        fill="none"
        stroke="#9CA3AF"
        strokeWidth={1.5}
      />
      <circle cx={x(series.length - 1)} cy={y(series[series.length - 1])} r={2.5} fill="#0B69D4" />
    </svg>
  );
}
